// src/components/Login.js
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import logo from '../assets/logo-768x114.png';
import ReCAPTCHA from 'react-google-recaptcha';

const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
  '6LdYm8MsAAAAADo_Rm9P9s5jwSdvelLughZaMtt7';
const GZY_WEBSITE_URL = 'https://www.gzy92fs.com/';

const Login = () => {
  const { login } = useAuthContext();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const onCaptchaChange = (token) => {
    setCaptchaToken(token);
    setError('');
  };

  // Convierte cualquier error del backend en string seguro para React
  function parseErrorMessage(err) {
    // fetch: err puede ser { status, data }
    if (err?.data) {
      const d = err.data.detail;
      if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join('; ');
      if (typeof d === 'object' && d !== null) return d.msg || JSON.stringify(d);
      if (typeof d === 'string') return d;
    }

    // axios: por si en otro flujo cae aquí
    if (err?.response) {
      const d = err.response.data?.detail;
      if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join('; ');
      if (typeof d === 'object' && d !== null) return d.msg || JSON.stringify(d);
      if (typeof d === 'string') return d;
      if (err.response.status === 401) return 'Credenciales incorrectas';
      if (err.response.status === 403) return 'Acceso denegado';
    }

    return err?.message || 'Error al iniciar sesión';
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password, captchaToken);
      navigate('/dashboard');
    } catch (err) {
      console.log('Login error:', err);

      const message = parseErrorMessage(err);
      const isLicensePending =
        err?.licensePending === true ||
        (typeof message === 'string' && message.toLowerCase().includes('licencia pendiente'));

      if (isLicensePending) {
        navigate('/license-activation', {
          state: {
            pendingEmail: email,
            pendingPassword: password,
            pendingCaptchaToken: captchaToken,
          },
        });
        return;
      }

      // ¿Backend pide captcha?
      const needsCaptcha = err?.needsCaptcha === true;
      if (needsCaptcha) setShowCaptcha(true);

      setError(message);

      // Reset captcha si estaba visible
      if (showCaptcha && captchaRef.current) {
        captchaRef.current.reset();
        setCaptchaToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      <img src={logo} alt="Logo" className="mx-auto mb-8 h-20 md:h-24 drop-shadow-[0_0_20px_rgba(255,255,255,0.18)]" />
      <h1 className="text-3xl font-display font-bold text-center text-white mb-2 uppercase tracking-wide">Bienvenido/a</h1>
      <p className="text-center text-gray-300 mb-8">Inicia sesión para acceder a tu plan.</p>

      <form onSubmit={handleSubmit} className="glass-panel p-8 rounded-2xl">
        <div className="mb-6">
          <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-300">
            Correo Electrónico
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="tu@email.com"
            className="bg-gray-800/80 border border-gray-600 text-white text-sm rounded-lg focus:ring-marizan-red focus:border-marizan-red block w-full p-2.5 transition-colors"
          />
        </div>

        <div className="mb-6">
          <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-300">
            Contraseña
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            placeholder="••••••••"
            className="bg-gray-800/80 border border-gray-600 text-white text-sm rounded-lg focus:ring-marizan-red focus:border-marizan-red block w-full p-2.5 transition-colors"
          />
        </div>

        {showCaptcha && (
          <div className="mb-6 flex justify-center">
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={onCaptchaChange}
              theme="dark"
            />
          </div>
        )}

        {showCaptcha && !captchaToken && (
          <p className="text-xs text-gray-400 text-center mb-4">
            Completa el captcha para continuar con el inicio de sesión.
          </p>
        )}

        {error && (
          <p className="text-red-300 bg-red-950/40 border border-red-900/60 rounded-lg px-3 py-2 text-sm text-center mb-4">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading || (showCaptcha && !captchaToken)}
          className="w-full text-white bg-marizan-red hover:bg-red-700 focus:ring-4 focus:outline-none focus:ring-red-800 font-bold rounded-lg text-sm px-5 py-3 text-center uppercase transition-colors duration-300 disabled:bg-gray-500 disabled:cursor-not-allowed"
        >
          {loading ? 'Ingresando...' : 'Ingresar'}
        </button>
      </form>

      <p className="text-center text-gray-400 mt-6">
        ¿No tienes una cuenta?{' '}
        <a
          href="https://www.marizanfitness.com"
          className="font-semibold text-marizan-red hover:text-red-700 transition-colors"
          target="_blank"
          rel="noopener noreferrer"
        >
          Solicítalo en MarizanFitness.com
        </a>
      </p>

      <footer className="mt-4 text-center text-xs text-gray-300">
        Desarrollado por gZy.Code ·{' '}
        <a
          href={GZY_WEBSITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.preventDefault();
            window.open(GZY_WEBSITE_URL, '_blank', 'noopener,noreferrer');
          }}
          className="text-gray-200 hover:text-marizan-red transition-colors"
        >
          www.gzy92fs.com
        </a>
      </footer>
    </div>
  );
};

export default Login;
